// `documents`: Swift `CompanyDocument` (GroundTeam/Models/Record.swift): company-wide files (price sheets, brochures).
// The file lives in the `company-files` bucket at `<company lowercase>/documents/<fileName>` (envelope `file_path`,
// WEB_PLAN §0, §3.9). Decoding template `CompanyDocument(name: "", fileName: "")`
// (GroundTeam/Sync/ResilientDecoding.swift:109).

import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  DATE,
  STRING,
  UUID,
  createModel,
  decodeModel,
  field,
  nowWire,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface DocumentPayload extends Passthrough {
  id: string;
  name: string;
  /** Object name inside `<company>/documents/`. */
  fileName: string;
  category: string;
  addedAt: string;
  addedBy?: string;
}

export const DOCUMENT_KEYS = [
  'id',
  'name',
  'fileName',
  'category',
  'addedAt',
  'addedBy',
] as const satisfies readonly (keyof Known<DocumentPayload>)[];

export const DEFAULT_DOCUMENT_CATEGORY = 'General';

export const DOCUMENT_FIELDS: FieldMap<DocumentPayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  fileName: field.req(STRING, () => ''),
  category: field.req(STRING, () => DEFAULT_DOCUMENT_CATEGORY),
  addedAt: field.req(DATE, nowWire),
  addedBy: field.opt(UUID),
};

export function decodeDocument(raw: JsonObject, envelopeId?: string): Decoded<DocumentPayload> {
  return decodeModel(DOCUMENT_FIELDS, raw, {}, envelopeId);
}

export type DocumentInit = Partial<Known<DocumentPayload>> & { name: string; fileName: string };

export function newDocument(init: DocumentInit): DocumentPayload {
  return createModel(
    DOCUMENT_FIELDS,
    { id: newId(), category: DEFAULT_DOCUMENT_CATEGORY, addedAt: nowWire() },
    init,
    'documents',
  );
}
